import { Button, Flex, Form, Input, Select } from "antd";
import { useEffect } from "react";

import CustomDrawer from "../../../../../../components/ui/CustomDrawer";
import {
  useGetBankListQuery,
  useUpdatePaymentInfoMutation,
} from "../../../../../../features/Vendor/vendor.api";
import useError from "../../../../../../hooks/useError";

import { CustomModalProps } from "../../../../../../types";
import {
  selectArrayOption,
  selectSearchOption,
} from "../../../../../../utils/utils";
import {
  validateBankAccountName,
  validateMobileBankAccount,
} from "../../../../../../utils/validators";
import { ModalData, ModalType } from "../Payments";

type Props = {
  remove: (val: number) => void;
  data: ModalData;
  type: ModalType;
  onFinish: (values: any) => void;
  defaultValues?: any;
} & CustomModalProps;

const ManageMFSDrawer = ({
  open,
  remove,
  data,
  type,
  handleClose: onClose,
  onFinish,
  defaultValues,
}: Props) => {
  const [mfsForm] = Form.useForm();
  const [updateBank, { isLoading, isError, error }] =
    useUpdatePaymentInfoMutation();

  const { data: mfsLists } = useGetBankListQuery({
    params: { type: "mfs", status: "active" },
  });

  const mfsList = mfsLists?.data?.length
    ? {
        data: [
          {
            name_en: "Sheba Pay",
            keyword: "SHEBA_PAY",
            logo: "https://sheba-dev-digigo-bucket.s3.ap-southeast-1.amazonaws.com/upload/1760267900086-sheba%20pay.png",
          },
          ...mfsLists?.data,
        ],
      }
    : { data: [] };

  const selectedMFSType = Form.useWatch(["mfsAccount", "mfs_type"], mfsForm);

  // ----- Handlers -----
  const resetForm = () => mfsForm.resetFields();

  const handleFinish = async (values: any) => {
    if (type === "UPDATE-MFS" && defaultValues?.id) {
      const result = await updateBank({
        vendorId: defaultValues?.vendor_id,
        paymentInfoId: defaultValues?.id,
        data: values,
      }).unwrap();

      if (result?.issuccess) {
        onFinish(values);
        resetForm();
      }
    } else {
      onFinish(values);
      resetForm();
    }
  };

  const handleClose = () => {
    if (type === "ADD-MFS") remove(data?.index as number);
    resetForm();
    onClose?.();
  };

  // ----- Effects -----
  useEffect(() => {
    if (type === "UPDATE-MFS") {
      mfsForm.setFieldsValue(defaultValues);
    }
  }, [type, mfsForm, defaultValues]);

  useEffect(() => {
    if (selectedMFSType) {
      const bankData = mfsList?.data?.find(
        (bank) => bank.name_en === selectedMFSType,
      );

      if (bankData) {
        mfsForm.setFieldsValue({
          mfsAccount: {
            bank_keyword: bankData.keyword,
            mfs_logo: bankData.logo,
          },
        });
      }
    }
  }, [selectedMFSType, mfsForm, mfsList?.data]);

  // ----- Error Handler -----
  useError(isError, error, "MFS update failed.");

  return (
    <CustomDrawer
      title={type === "ADD-MFS" ? "Add MFS" : "Update MFS"}
      open={open}
      onClose={handleClose}
    >
      <Form
        className="custom-form"
        layout="vertical"
        // requiredMark="optional"
        size="large"
        form={mfsForm}
        onFinish={handleFinish}
      >
        {/* Hidden Fields */}
        <Form.Item name="payment_type" initialValue="MFS" hidden>
          <Input />
        </Form.Item>
        <Form.Item name={["mfsAccount", "mfs_logo"]} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={["mfsAccount", "bank_keyword"]} hidden>
          <Input />
        </Form.Item>

        {/* Visible Fields */}
        <Form.Item
          className="mb-2"
          label="MFS Type"
          name={["mfsAccount", "mfs_type"]}
          rules={[{ required: true, message: "Please select MFS type" }]}
        >
          <Select
            filterOption={selectSearchOption}
            options={selectArrayOption(
              mfsList?.data || [],
              "name_en",
              "name_en",
            )}
            placeholder="Select MFS type"
          />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Account Name"
          name={["mfsAccount", "account_name"]}
          rules={[
            { required: true, message: "Please enter account name" },
            { validator: validateBankAccountName },
          ]}
        >
          <Input placeholder="Enter account name" />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Account Number"
          name={["mfsAccount", "account_number"]}
          rules={[
            { required: true, message: "Please enter account number" },
            { validator: validateMobileBankAccount },
          ]}
        >
          <Input placeholder="Enter account number" />
        </Form.Item>

        {/* Actions */}
        <Flex gap={16} justify="end">
          <Button
            onClick={handleClose}
            size="small"
            className="button-white-bordered"
          >
            Cancel
          </Button>
          <Button
            size="small"
            loading={isLoading}
            className="button-primary min-w-20"
            htmlType="button"
            onClick={() => mfsForm.submit()}
          >
            {type === "ADD-MFS" ? "Add" : "Update"}
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManageMFSDrawer;
